import type { Location } from 'react-router-dom';
import { getSamdOrderListColumns } from '@samd/features/orders';
import {
  SAMD_STATUS_FILTER_OPTIONS,
  samdOrderListSearchConfig,
} from '@samd/features/orders/statuses';
import { OrderStatusUtils } from '@samd/features/orders/utils/orders';
import type {
  OrderListRowLinkFields,
  WorkflowDefinition,
} from 'src/distribution_platforms/features/workflow_definition';
import { toOrderListColumns } from 'src/distribution_platforms/features/workflow_definition';
import { SpecimenType, SlideImageScannerType, OrderStatus } from 'src/types';
import { WorkflowType } from 'src/types/workflows';

/** SaMD workflow definition. */
export interface SamdWorkflowDefinition extends WorkflowDefinition {
  getSupportedSlideScanners(
    specimenType: SpecimenType,
  ): Set<SlideImageScannerType>;
  /** URL for the Instructions for Use (IFU) PDF. */
  instructionsPdfUrl: string;
  /** Whether the help menu should include a User Manual item. */
  showUserManualInHelp: boolean;
  /** Support contact email for this workflow. */
  supportEmail: string;
}

export const SAMD_US_WORKFLOW: SamdWorkflowDefinition = {
  canCreateOrder: true,
  canEditOrder,
  getRowLink,
  orderListSearchConfig: samdOrderListSearchConfig,
  getStatusFilterOptions: () => SAMD_STATUS_FILTER_OPTIONS,
  supportedSpecimenTypes: new Set([
    SpecimenType.ProstateBiopsy,
    SpecimenType.BreastBiopsy,
  ]),
  getStatusDisplayName: OrderStatusUtils.getOrderStatusName,
  getSupportedSlideScanners: (specimenType) => {
    switch (specimenType) {
      case SpecimenType.ProstateBiopsy:
        return new Set([SlideImageScannerType.PhilipsUltraFast]);
      case SpecimenType.BreastBiopsy:
        return new Set([
          SlideImageScannerType.PhilipsUltraFast,
          SlideImageScannerType.LeicaGt450Dx,
        ]);
      default:
        throw new Error(`Unsupported specimen type: ${specimenType}`);
    }
  },
  getOrderListColumns: () =>
    toOrderListColumns(getSamdOrderListColumns(WorkflowType.SAMD_US)),
  instructionsPdfUrl: '/content/ArteraAI_US_IFU_v4.1.pdf',
  showUserManualInHelp: true,
  supportEmail: 'support@artera.ai',
};

export const SAMD_UK_WORKFLOW: SamdWorkflowDefinition = {
  canCreateOrder: true,
  canEditOrder,
  getRowLink,
  orderListSearchConfig: samdOrderListSearchConfig,
  getStatusFilterOptions: () => SAMD_STATUS_FILTER_OPTIONS,
  supportedSpecimenTypes: new Set([
    SpecimenType.ProstateBiopsy,
    SpecimenType.BreastBiopsy,
  ]),
  getStatusDisplayName: OrderStatusUtils.getOrderStatusName,
  getSupportedSlideScanners: (_specimenType) =>
    new Set([
      SlideImageScannerType.GrundiumOcus_20,
      SlideImageScannerType.HamamatsuNanozoomerS360,
      SlideImageScannerType.PhilipsUltraFast,
      SlideImageScannerType.LeicaGt450Dx,
      SlideImageScannerType.KfbioPro,
    ]),
  getOrderListColumns: () =>
    toOrderListColumns(getSamdOrderListColumns(WorkflowType.SAMD_UK)),
  instructionsPdfUrl:
    'https://cdn-uk.artera.ai/ArteraAI SaMD Platform IFU v9.0 [UK].pdf',
  showUserManualInHelp: true,
  supportEmail: 'support-uk@artera.ai',
};

export const SAMD_EU_WORKFLOW: SamdWorkflowDefinition = {
  canCreateOrder: true,
  canEditOrder,
  getRowLink,
  orderListSearchConfig: samdOrderListSearchConfig,
  getStatusFilterOptions: () => SAMD_STATUS_FILTER_OPTIONS,
  supportedSpecimenTypes: new Set([
    SpecimenType.ProstateBiopsy,
    SpecimenType.BreastBiopsy,
  ]),
  getStatusDisplayName: OrderStatusUtils.getOrderStatusName,
  getSupportedSlideScanners: (_specimenType) =>
    new Set([
      SlideImageScannerType.GrundiumOcus_20,
      SlideImageScannerType.HamamatsuNanozoomerS360,
      SlideImageScannerType.PhilipsUltraFast,
      SlideImageScannerType.LeicaGt450Dx,
      SlideImageScannerType.KfbioPro,
    ]),
  getOrderListColumns: () =>
    toOrderListColumns(getSamdOrderListColumns(WorkflowType.SAMD_EU)),
  instructionsPdfUrl:
    'https://cdn-eu.artera.ai/ArteraAI SaMD Platform IFU v4.0 [EU].pdf',
  showUserManualInHelp: true,
  supportEmail: 'support-eu@artera.ai',
};

export const SAMD_DEMO_WORKFLOW: SamdWorkflowDefinition = {
  canCreateOrder: true,
  canEditOrder,
  getRowLink,
  orderListSearchConfig: samdOrderListSearchConfig,
  getStatusFilterOptions: () => SAMD_STATUS_FILTER_OPTIONS,
  supportedSpecimenTypes: new Set([SpecimenType.ProstateBiopsy]),
  getStatusDisplayName: OrderStatusUtils.getOrderStatusName,
  getSupportedSlideScanners: (_specimenType) =>
    new Set([
      SlideImageScannerType.LeicaGt450Dx,
      SlideImageScannerType.HamamatsuNanozoomerS360Md,
    ]),
  getOrderListColumns: () =>
    toOrderListColumns(getSamdOrderListColumns(WorkflowType.SAMD_DEMO)),
  // The demo portal doesn't show the IFU, so we're using the US IFU as a placeholder.
  instructionsPdfUrl: '/content/ArteraAI_US_IFU_v4.1.pdf',
  showUserManualInHelp: false,
  supportEmail: 'support@artera.ai',
};

/**
 * Returns the SaMD workflow definition for the given workflow type.
 */
export function getSamdWorkflowDefinition(
  workflowType: WorkflowType,
): SamdWorkflowDefinition {
  switch (workflowType) {
    case WorkflowType.SAMD_US:
      return SAMD_US_WORKFLOW;
    case WorkflowType.SAMD_UK:
      return SAMD_UK_WORKFLOW;
    case WorkflowType.SAMD_EU:
      return SAMD_EU_WORKFLOW;
    case WorkflowType.SAMD_DEMO:
      return SAMD_DEMO_WORKFLOW;
    default:
      throw new Error(`Unsupported SaMD workflow type: ${workflowType}`);
  }
}

function canEditOrder(order: OrderListRowLinkFields, _userId: string): boolean {
  return order.status === OrderStatus.Draft;
}

function getRowLink(
  order: OrderListRowLinkFields,
  organization: string,
  location: Location,
  userId: string,
): {
  pathname: string;
  state: { background: { pathname: string; search: string } };
} {
  if (canEditOrder(order, userId)) {
    return {
      pathname: `/${organization}/orders/${order.id}/edit`,
      state: {
        background: { pathname: location.pathname, search: location.search },
      },
    };
  }

  return {
    pathname: `/${organization}/orders/${order.id}`,
    state: {
      background: { pathname: location.pathname, search: location.search },
    },
  };
}
